import { useEffect, useRef, useState } from 'react';
import { Building2, Check, Plus } from 'lucide-react';
import Button from 'components/crm/ui/Button';
import Spinner from 'components/crm/ui/Spinner';
import useText from 'hooks/useText';
import { globalMotif, sourceKey } from 'pages/prospection/callListItems';

const GlobalListRow = ({ prospect, added, onAdd }) => {
  const { text } = useText('prospection');
  const [pending, setPending] = useState(false);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const { company } = prospect;
  const sourceLabelKey = sourceKey(company.source);
  const subline =
    [company.contactName, company.city].filter(Boolean).join(' · ') ||
    (sourceLabelKey ? text(sourceLabelKey) : '');
  const motif = globalMotif(prospect);
  // globalMotif already picks the One/Other key: going through plural() would
  // append the suffix a second time and read a missing key.
  const motifText = text(
    motif.key,
    motif.count === undefined
      ? motif.values
      : { count: motif.count, ...motif.values },
  );

  const handleAdd = async () => {
    setPending(true);
    try {
      await onAdd(prospect);
    } catch (error) {
      // The parent reports the failure; the button just comes back.
    } finally {
      if (mounted.current) setPending(false);
    }
  };

  return (
    <li className="flex items-center gap-3 border-b border-line py-3 last:border-b-0">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-line bg-white text-ink">
        <Building2 size={18} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm font-medium leading-5 text-ink">
          {company.name}
        </span>
        <span className="truncate text-xs leading-4 text-body">{subline}</span>
        <span className="truncate text-xs leading-4 text-muted">
          {motifText}
        </span>
      </div>
      {added ? (
        <span className="flex shrink-0 items-center gap-1 px-3 text-sm font-medium text-success">
          <Check size={16} strokeWidth={2.5} />
          {text('global.added')}
        </span>
      ) : (
        <Button
          variant="secondary"
          icon={pending ? undefined : Plus}
          disabled={pending}
          className="shrink-0"
          onClick={handleAdd}
        >
          {pending && <Spinner />}
          {text('global.add')}
        </Button>
      )}
    </li>
  );
};

export default GlobalListRow;
